import type { ISettingsProps } from "./Models.ts";
import { ladderForSettings, ladderTitle, levelApplies, levelName } from "./FactLevels.ts";
import { printedLanguageOf } from "./PrintedLanguage.ts";
import { pdfChrome } from "../Shared/Pdf/chrome.ts";

/**
 * What the tracking chart's document is built from (ADR 0071 §8), as plain data: the title, the
 * names, and each level's heading as a finished string. Built here, on the main thread, from the
 * ladder the sheet is on, so the document builder needs nothing but the renderer.
 *
 * `null` when levels do not apply, since there is then no ladder to chart. The roster's levels are
 * deliberately not printed: the boxes are the teacher's.
 *
 * `FluencyChartProps` is declared here, not in the document, so nothing that builds these props
 * has an import path to the renderer (`bundleBoundary.test.js` walks static imports, types too).
 */
export interface FluencyChartProps {
    title: string;
    /** One row each, as typed. Empty: 25 blank rows with a line to write a name on. */
    names: string[];
    levels: { id: number; label: string }[];
    /** The name column's heading, in the sheet's language. */
    labels: { name: string };
}

export function fluencyChartProps(settings: ISettingsProps, names: string[]): FluencyChartProps | null {
    if (!levelApplies(settings)) {
        return null;
    }
    const ladder = ladderForSettings(settings)!;
    const language = printedLanguageOf(settings);
    const chrome = pdfChrome(language);
    return {
        title: chrome.chartTitle(ladderTitle(ladder.id, language)),
        names,
        levels: ladder.levels.map((level) => ({ id: level.id, label: levelName(level, ladder.id, language) })),
        labels: { name: chrome.chartName },
    };
}
